import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import SignaturePad from "react-signature-canvas";
import { Button, Alert } from "reactstrap";
import { signPdf, uploadSignedDocument } from "helpers/fakebackend_helper";
import { BASE_URL, FILES_URL } from "helpers/url_helper";
import PDFPagePrevNextButtons from "./PDFPagePrevNextButtons";
import StepCompleteActionButton from "./StepCompleteActionButton";
import CameraController from "./CameraController";
import UploadSignedDocumentControls from "./UploadSignedDocumentControls";
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const SignPDFFormStep = ({ step, email, token, onNext }) => {
    const pdfFileName = step?.employee_file?.file_name || "";
    const [pdfUrl] = useState(`${FILES_URL}${pdfFileName}`);
    const [numPages, setNumPages] = useState(null);
    const [isSigned, setIsSigned] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const sigPadRef = useRef(null);
    const pageRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [signedPdfUrl, setSignedPdfUrl] = useState("");
    const [pdfData, setPdfData] = useState(null);
    const [signedPdfData, setSignedPdfData] = useState(null);
    const [overridePdfUrl, setOverridePdfUrl] = useState("");
    const [isPreparedMode, setIsPreparedMode] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [printClicked, setPrintClicked] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const arrayBuffer = await fetch(pdfUrl).then(r => r.arrayBuffer());
                setPdfData(arrayBuffer);
            } catch (e) {
                setError(e.message || "Failed to load PDF.");
            }
        };

        load();
    }, [pdfUrl]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setCurrentPage(1);
    };

    const onSubmit = async () => {
        setError("");

        const signatureData = sigPadRef.current.getCanvas().toDataURL("image/png");

        setLoading(true);

        try {
            const finalUrl = overridePdfUrl || pdfUrl;
            const pdfArrayBuffer = await fetch(finalUrl).then(r => r.arrayBuffer());
            const pdfBase64 = btoa(
                new Uint8Array(pdfArrayBuffer).reduce((d, b) => d + String.fromCharCode(b), "")
            );

            const payload = {
                pdf: pdfBase64,
                signature: signatureData,
                pdf_width: pageRef.current.pdfWidth,
                pdf_height: pageRef.current.pdfHeight,
                email: email,
                employee_file_id: step?.employee_file_id,
                prefilled_pdf_signed: step.employee_file.prefill_needed === 1 && isPreparedMode ? 1 : 0
            };

            const res = await signPdf(token, payload);

            if (res.code === 1) {
                const url = `${BASE_URL}/users/signed-files/${res.file_id}/${token}`;
                setSignedPdfUrl(url);

                const signedBuffer = await fetch(url).then(r => r.arrayBuffer());
                setSignedPdfData(signedBuffer);

                setLoading(false);
            } else {
                setLoading(false);
                setError(res.message || "Failed to sign PDF.");
            }
        } catch (err) {
            setLoading(false);
            setError(err.message || "Unexpected error");
        }
    };

    const uploadTakenPicture = async (img) => {
        setCapturedImage(img);
        setShowCamera(false)
        setLoading(true);
        setError("");

        const blob = await (await fetch(img)).blob();

        const payload = {
            email,
            employee_file_id: step?.employee_file_id,
            file: blob
        };

        const res = await uploadSignedDocument(token, payload);

        if (res.code === 1) {
            setLoading(false);
            onNext();
        } else {
            setLoading(false);
            setError(res.message || "Upload failed");
        }
    }

    const uploadSignedDoc = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        setError("");

        const payload = {
            email,
            employee_file_id: step?.employee_file_id,
            file
        };

        const res = await uploadSignedDocument(token, payload);

        if (res.code === 1) {
            setLoading(false);
            onNext();
        } else {
            setLoading(false);
            setError(res.message || "Upload failed");
        }
    }

    if (signedPdfUrl) {
        return (
            <>
                <h1 className="fw-semibold mb-3">PDF Signed Successfully</h1>

                {/* PAGE CONTROLS */}
                {numPages > 1 && (
                    <PDFPagePrevNextButtons
                        currentPage={currentPage}
                        numPages={numPages}
                        setCurrentPage={setCurrentPage}
                    />
                )}

                <div
                    style={{
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        marginBottom: 20,
                        width: "100%",
                        display: "flex",
                        justifyContent: "center"
                    }}
                >
                    <Document file={signedPdfData} onLoadSuccess={onDocumentLoadSuccess}>
                        <Page
                            pageNumber={currentPage}
                            width={Math.min(window.innerWidth - 40, 500)}
                            renderAnnotationLayer={false}
                            renderTextLayer={false}
                        />
                    </Document>
                </div>

                <StepCompleteActionButton
                    label="CONTINUE"
                    onClick={onNext}
                />

                <Button
                    className="mt-2 w-100"
                    color="secondary"
                    style={{ padding: "0.8rem", fontSize: "1rem", fontWeight: 600 }}
                    onClick={() => setSignedPdfUrl("")}
                >
                    SIGN AGAIN
                </Button>
            </>
        );
    }

    if (step?.employee_file?.prefill_needed === 1 && !isPreparedMode) {
        return (
            <>
                {error && <Alert color="danger">{error}</Alert>}

                <h1 className="text-dark mb-4 fw-semibold">{step.step_title}</h1>
                <h6 className="text-dark mb-4" style={{ whiteSpace: "pre-line" }}>{step.step_description}</h6>

                <StepCompleteActionButton
                    label={preparing ? "PREPARING..." : "AUTOFILL THE FORM"}
                    disabled={preparing}
                    onClick={async () => {
                        try {
                            setPreparing(true);
                            setError("");

                            const finalUrl = pdfUrl; // same as normal file
                            const pdfArrayBuffer = await fetch(finalUrl).then(r => r.arrayBuffer());

                            const pdfBase64 = btoa(
                                new Uint8Array(pdfArrayBuffer).reduce((acc, b) => acc + String.fromCharCode(b), "")
                            );

                            const payload = {
                                pdf: pdfBase64,
                                signature: null,
                                pdf_width: pageRef.current?.pdfWidth || 800,
                                pdf_height: pageRef.current?.pdfHeight || 1100,
                                email,
                                employee_file_id: step?.employee_file_id,
                                prefilled_pdf_signed: 0
                            };

                            const res = await signPdf(token, payload);

                            if (res.code !== 1) {
                                setPreparing(false);
                                setError(res.message || "Failed to autofill PDF.");
                                return;
                            }

                            const url = `${BASE_URL}/users/signed-files/${res.file_id}/${token}`;
                            setOverridePdfUrl(url);

                            const arr = await fetch(url).then(r => r.arrayBuffer());
                            setPdfData(arr);

                            setPreparing(false);
                            setIsPreparedMode(true);
                        } catch (err) {
                            setPreparing(false);
                            setError(err.message || "Autofill failed.");
                        }
                    }}
                />
            </>
        );
    }

    if (step?.employee_file?.print_needed === 1) {

        if (showCamera) {
            return (
                <CameraController
                    preview={null}
                    onPhotoTaken={uploadTakenPicture}
                    onUpload={() => { }}
                    onRetake={() => { }}
                    onCancel={() => setShowCamera(false)}
                />
            );
        }

        return (
            <>
                {error && <Alert color="danger">{error}</Alert>}

                <h1 className="text-dark mb-4 fw-semibold">{step.step_title}</h1>
                <h6 className="text-dark mb-4" style={{ whiteSpace: "pre-line" }}>{step.step_description}</h6>

                {/* PRINT BUTTON */}
                <div className="text-center mb-3">
                    <StepCompleteActionButton
                        label={printClicked ? "PRINT AGAIN" : "PRINT"}
                        onClick={() => {
                            window.open(overridePdfUrl, "_blank")
                            setPrintClicked(true);
                        }}
                    />
                </div>

                {/* PAGE CONTROLS */}
                {numPages > 1 && (
                    <PDFPagePrevNextButtons
                        currentPage={currentPage}
                        numPages={numPages}
                        setCurrentPage={setCurrentPage}
                    />
                )}

                {/* PDF VIEW */}
                <div
                    style={{
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        marginBottom: 20,
                        width: "100%",
                        display: "flex",
                        justifyContent: "center"
                    }}
                >
                    <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess}>
                        <Page
                            pageNumber={currentPage}
                            width={Math.min(window.innerWidth - 40, 500)}
                            renderAnnotationLayer={false}
                            renderTextLayer={false}
                        />
                    </Document>
                </div>

                {/* UPLOAD FILE + TAKE PICTURE */}
                {printClicked && (
                    <UploadSignedDocumentControls
                        onUploadFile={uploadSignedDoc}
                        onTakePicture={() => setShowCamera(true)}
                    />
                )}
            </>
        );
    }

    return (
        <>
            {error && (
                <Alert color="danger" className="text-start">
                    {error}
                </Alert>
            )}

            <h1 className="text-dark mb-4 fw-semibold">{step.step_title}</h1>
            <h6 className="text-dark mb-4" style={{ whiteSpace: "pre-line" }}>{step.step_description}</h6>

            {/* PAGE CONTROLS */}
            {numPages > 1 && (
                <PDFPagePrevNextButtons
                    currentPage={currentPage}
                    numPages={numPages}
                    setCurrentPage={setCurrentPage}
                />
            )}

            {/* PDF VIEW */}
            <div
                ref={pageRef}
                style={{
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    marginBottom: 20,
                    cursor: "crosshair",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center"
                }}
                onClick={(e) => {
                    if (!pageRef.current) return;

                    const rect = pageRef.current.getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    const clickY = e.clientY - rect.top;

                    const pdfX = (clickX / rect.width) * pageRef.current.originalWidth;
                    const pdfY = (clickY / rect.height) * pageRef.current.originalHeight;

                    console.log("CLICK PX:", {
                        clickX,
                        clickY,
                        pdfX: Math.round(pdfX),
                        pdfY: Math.round(pdfY),
                        page: currentPage
                    });
                }}
            >
                <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess}>
                    <Page
                        pageNumber={currentPage}
                        width={Math.min(window.innerWidth - 40, 500)}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                        onRenderSuccess={(page) => {
                            const viewport = page.getViewport({ scale: 1 });

                            pageRef.current.pdfWidth = viewport.width;
                            pageRef.current.pdfHeight = viewport.height;

                            pageRef.current.originalWidth = page.originalWidth;
                            pageRef.current.originalHeight = page.originalHeight;
                        }}
                    />
                </Document>
            </div>

            {/* PAGE CONTROLS */}
            {numPages > 1 && (
                <PDFPagePrevNextButtons
                    currentPage={currentPage}
                    numPages={numPages}
                    setCurrentPage={setCurrentPage}
                />
            )}

            {/* SIGNATURE PAD */}
            <div style={{ marginBottom: 15 }}>
                <div style={{ textAlign: "center", marginBottom: 8 }}>
                    <Button
                        size="sm"
                        color="danger"
                        onClick={() => {
                            sigPadRef.current.clear();
                            setIsSigned(false);
                        }}
                        style={{ marginBottom: 6 }}
                    >
                        CLEAR
                    </Button>
                </div>
                <div>
                    <h6 className="text-dark" style={{ whiteSpace: "pre-line" }}>{step.employee_file.attestation_text || "SIGN HERE"}</h6>
                </div>

                <SignaturePad
                    ref={sigPadRef}
                    onEnd={() => setIsSigned(!sigPadRef.current.isEmpty())}
                    canvasProps={{
                        width: 400,
                        height: 200,
                        style: {
                            backgroundColor: "#ffffff",
                            borderRadius: 6,
                            border: "1px solid #ccc"
                        }
                    }}
                />
            </div>

            <StepCompleteActionButton
                label={loading ? "LOADING..." : "SIGN & SUBMIT"}
                onClick={onSubmit}
                disabled={loading || !isSigned}
            />
        </>
    );
};

export default SignPDFFormStep;